import React, { useState, useEffect, Suspense } from 'react';
import { Routes, Route, useLocation, Navigate, useNavigate, useParams } from 'react-router-dom';
import CollectionsPage from './components/pages/CollectionsPage';
import CVPage from './components/pages/CVPage';
import ContentPage from './components/pages/ContentPage';
import Header from './components/layout/Header';
import NotFound from './NotFound';
import { loadProjects } from './utils/dataLoader';
import { debugLog } from './utils/debug';
import { initViewportHeight } from './utils/viewportHeight';
import { AuthProvider, useAuth } from './utils/AuthContext';
import Loading from './components/ui/Loading';
import ProtectedRoute from './components/ui/ProtectedRoute';
import './App.scss';
import { HelmetProvider } from 'react-helmet-async';
import { config, routes } from './utils/routingConfig';
import ImprintPage from './components/pages/ImprintPage';
import BrandPage from './components/pages/BrandPage';
import FooterWrapper from './components/ui/FooterWrapper';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from "@vercel/speed-insights/react"

// Redirect component for old gallery routes
const GalleryRedirect = () => {
  const { projectId } = useParams();
  return <Navigate to={`/${projectId}`} replace />;
};

const HeaderWrapper = ({ projects, currentProject, onProjectSelect }) => {
  const location = useLocation();
  const hostname = window.location.hostname;
  const isRentalSubdomain = hostname.startsWith('rent.');
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isAboutPage = location.pathname === '/about' || location.pathname.startsWith('/about');
  const isContentPage = ['/terms', '/privacy', '/shipping', '/imprint', '/brand'].includes(location.pathname);

  if (isRentalSubdomain || isAdminRoute) {
    return null;
  }

  const isHomePage = location.pathname === '/';
  if (isHomePage) {
    return null;
  }

  const projectToShow = isAboutPage
    ? projects.find(p => p.id === 'about')
    : isContentPage
      ? projects.find(p => p.id === 'about')
      : currentProject;

  if (!projects || projects.length === 0) {
    return null;
  }

  if (isContentPage && !projectToShow) {
    const fallbackProject = projects[0];
    if (!fallbackProject) return null;
    return (
      <Header
        projects={projects}
        project={fallbackProject}
        onProjectSelect={onProjectSelect}
        showBackToTimeline={false}
      />
    );
  }

  if (!projectToShow) {
    return null;
  }

  return (
    <Header
      projects={projects}
      project={projectToShow}
      onProjectSelect={onProjectSelect}
      showBackToTimeline={false}
    />
  );
};

const LoginPage = React.lazy(() => import('./components/rental/LoginPage'));
const RentalDashboard = React.lazy(() => import('./components/rental/RentalDashboard'));
const NewBooking = React.lazy(() => import('./components/rental/NewBooking'));
const CreateAgreement = React.lazy(() => import('./components/rental/CreateAgreement'));

const RentalRootRedirect = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading) {
      navigate(isAuthenticated ? '/rental' : '/login', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  return <Loading />;
};

function App() {
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const hostname = window.location.hostname;
  const isRentalSubdomain = hostname.startsWith('rent.');

  config.isRentalSubdomain = isRentalSubdomain;

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      if (!isRentalSubdomain) {
        debugLog('Fetching full projects data...');
        const projectsData = await loadProjects();
        debugLog('Full projects data loaded:', projectsData.length, 'projects');
        setProjects(projectsData);
      }

      setIsLoading(false);
    };

    fetchData();
  }, [isRentalSubdomain, location.pathname]);

  useEffect(() => {
    if (selectedProject && !isRentalSubdomain) {
      document.documentElement.style.setProperty('--project-theme-color', selectedProject.themeColor);
    }
  }, [selectedProject, isRentalSubdomain]);

  useEffect(() => {
    const isCollectionsPageRoute = location.pathname === '/' ||
      location.pathname === '/about' ||
      location.pathname.match(/^\/[a-zA-Z-]+$/) &&
      !['/terms', '/privacy', '/shipping', '/imprint'].includes(location.pathname);

    if (!isCollectionsPageRoute && !isRentalSubdomain) {
      const themeColor = '#091115';
      const metaThemeColorTags = document.querySelectorAll('meta[name="theme-color"]');

      if (metaThemeColorTags.length > 0) {
        metaThemeColorTags.forEach((metaTag) => {
          metaTag.setAttribute('content', themeColor);
        });

        const firstMeta = metaThemeColorTags[0];
        if (firstMeta.parentNode) {
          const parent = firstMeta.parentNode;
          const nextSibling = firstMeta.nextSibling;
          parent.removeChild(firstMeta);
          void parent.offsetHeight;
          if (nextSibling) {
            parent.insertBefore(firstMeta, nextSibling);
          } else {
            parent.appendChild(firstMeta);
          }
          firstMeta.setAttribute('content', themeColor);
        }
      }
    }
  }, [location.pathname, isRentalSubdomain]);

  const handleProjectSelect = (projectId) => {
    const selected = projects.find(p => p.id === projectId);
    setSelectedProject(selected);
  };

  const showHeader = location.pathname !== '/';
  const infoPages = ['/terms', '/privacy', '/shipping', '/imprint', '/brand'];
  const isInfoPage = infoPages.includes(location.pathname);
  const isContentPageRoute = ['/terms', '/privacy', '/shipping', '/imprint', '/brand'].includes(location.pathname);
  const pageRendersHeader = (
    location.pathname === '/' ||
    location.pathname === '/about' ||
    (location.pathname.match(/^\/[a-zA-Z-]+$/) && !isContentPageRoute)
  );

  useEffect(() => {
    initViewportHeight();
  }, []);

  const renderRentalRoutes = () => (
    <>
      <Route path={routes.login} element={<LoginPage />} />
      <Route path="/" element={<RentalRootRedirect />} />
      <Route
        path={routes.rental}
        element={
          <ProtectedRoute>
            <RentalDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path={routes.NewBooking}
        element={
          <ProtectedRoute>
            <NewBooking />
          </ProtectedRoute>
        }
      />
      <Route
        path={routes.editReservation}
        element={
          <ProtectedRoute>
            <NewBooking editMode={true} />
          </ProtectedRoute>
        }
      />
      <Route
        path={routes.createAgreement}
        element={
          <ProtectedRoute>
            <CreateAgreement />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </>
  );

  if (isLoading) {
    return <Loading />;
  }

  const getLayoutClass = () => {
    if (isRentalSubdomain) return 'rental-app';

    const isImmersivePage = location.pathname === '/' ||
      location.pathname.startsWith('/gallery/') ||
      (location.pathname !== '/about' &&
       location.pathname !== '/about/cv' &&
       location.pathname !== '/terms' &&
       location.pathname !== '/privacy' &&
       location.pathname !== '/shipping' &&
       location.pathname !== '/imprint' &&
       location.pathname.match(/^\/[a-zA-Z-]+$/));

    return isImmersivePage ? 'immersive-layout' : '';
  };

  return (
    <HelmetProvider>
      <AuthProvider>
        <div className={`app-container ${getLayoutClass()}`} style={{
          backgroundColor: isRentalSubdomain
            ? '#091115'
            : (location.pathname.startsWith('/events/') || location.pathname === '/about' || isInfoPage)
              ? (projects.find(p => p.id === 'about')?.themeColor || '#091115')
              : (selectedProject?.themeColor || '#091115')
        }}>
          {!pageRendersHeader && (
            <HeaderWrapper
              projects={projects}
              currentProject={selectedProject}
              onProjectSelect={handleProjectSelect}
            />
          )}
          <main className={`main-content ${showHeader ? 'with-header' : ''}`}>
            <Suspense fallback={<Loading />}>
              <Routes>
                <Route
                  path={routes.admin}
                  element={<Navigate to="/" replace />}
                />

                {config.isRentalSubdomain ? (
                  renderRentalRoutes()
                ) : (
                    <>
                      <Route path="/" element={<CollectionsPage projects={projects} onProjectSelect={handleProjectSelect} />} />
                      <Route path="/about" element={<CollectionsPage projects={projects} onProjectSelect={handleProjectSelect} />} />

                      <Route path="/gallery" element={<Navigate to="/" replace />} />
                      <Route path="/gallery/:projectId" element={<GalleryRedirect />} />

                      <Route path="/about/cv" element={<CVPage data={projects.find(p => p.id === 'about')} />} />
                      <Route path="/terms" element={<ContentPage data={projects.find(p => p.id === 'terms')} pageTitle="Terms & Conditions" />} />
                      <Route path="/privacy" element={<ContentPage data={projects.find(p => p.id === 'privacy')} pageTitle="Privacy Policy" />} />
                      <Route path="/shipping" element={<ContentPage data={projects.find(p => p.id === 'shipping')} pageTitle="Shipping Information" />} />
                      <Route path="/imprint" element={<ImprintPage />} />
                      <Route path="/brand" element={<BrandPage />} />

                      <Route path="/:projectId" element={<CollectionsPage projects={projects} onProjectSelect={handleProjectSelect} />} />

                      <Route path="*" element={<NotFound />} />
                  </>
                )}
              </Routes>
            </Suspense>
          </main>
          <FooterWrapper />
          <Analytics />
          <SpeedInsights />
        </div>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
